import { ref, computed } from 'vue'

const MAX_UNDO_STACK = 5
const DEBOUNCE_MS = 600

export function useHtmlEditorUndo() {
  const undoStack = ref([])
  const isRestoring = ref(false)
  let debounceArmed = true
  let debounceTimer = null

  const canUndo = computed(() => undoStack.value.length > 0)

  function clearUndoStack() {
    undoStack.value = []
    debounceArmed = true
    if (debounceTimer) {
      window.clearTimeout(debounceTimer)
      debounceTimer = null
    }
  }

  function pushUndoSnapshot(snapshot) {
    if (isRestoring.value || !snapshot) return
    const stack = undoStack.value
    const top = stack[stack.length - 1]
    if (top && top.bodyHtml === snapshot.bodyHtml) return
    const next = [...stack, snapshot]
    if (next.length > MAX_UNDO_STACK) next.shift()
    undoStack.value = next
  }

  function popUndoSnapshot() {
    const stack = undoStack.value
    if (!stack.length) return null
    const snapshot = stack[stack.length - 1]
    undoStack.value = stack.slice(0, -1)
    return snapshot
  }

  function pushBeforeMutation(captureFn, { immediate = false } = {}) {
    if (isRestoring.value) return
    const doPush = () => {
      const snapshot = captureFn()
      if (snapshot) pushUndoSnapshot(snapshot)
    }
    if (immediate) {
      doPush()
      return
    }
    if (debounceArmed) {
      doPush()
      debounceArmed = false
    }
    if (debounceTimer) window.clearTimeout(debounceTimer)
    debounceTimer = window.setTimeout(() => {
      debounceTimer = null
      debounceArmed = true
    }, DEBOUNCE_MS)
  }

  async function undo(restoreFn) {
    const snapshot = popUndoSnapshot()
    if (!snapshot) return false
    isRestoring.value = true
    try {
      await restoreFn(snapshot)
      return true
    } finally {
      isRestoring.value = false
      debounceArmed = true
    }
  }

  return {
    canUndo,
    isRestoring,
    clearUndoStack,
    pushUndoSnapshot,
    pushBeforeMutation,
    undo,
  }
}
